import _LessonLibraryStartMutation from "../__generated__/LessonLibraryStartMutation.graphql";
import _LessonLibraryQuery from "../__generated__/LessonLibraryQuery.graphql";
import { Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, Navigate } from 'react-router-dom';
import { QuietBoundary } from '../components/QuietBoundary';
import { LessonTodayCard } from './LessonTodayCard';
import { t } from '../i18n/t';
import { En } from '../i18n/En';
import { localDay } from '../lessons/localDay.ts';
import { addDays, levelSummary, weekStrip } from '../lessons/progress.ts';
import { registerFor } from '../profile/register';
import { KindTile, SetRing, WeekStrip } from './LessonProgress';
import { kindLabel, levelGroups } from './lessonLabels.ts';
import type { LessonLibraryQuery as LessonLibraryQueryType } from '../__generated__/LessonLibraryQuery.graphql';
import type { LessonLibraryStartMutation } from '../__generated__/LessonLibraryStartMutation.graphql';
const LessonLibraryQuery = (_LessonLibraryQuery.hash && _LessonLibraryQuery.hash !== "347d2dfc8a5464669a3032c322c257c6" && console.error("The definition of 'LessonLibraryQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonLibraryQuery);
const StartSet = (_LessonLibraryStartMutation.hash && _LessonLibraryStartMutation.hash !== "1959fb956209bbf25eae04b124a0fce7" && console.error("The definition of 'LessonLibraryStartMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonLibraryStartMutation);

/**
 * The course's sets grouped by level, this week's marked with its day
 * (lesson library PRD §4.1), under today's card and the last seven days.
 * Each set wears a ring of the days it has been played on, out of the
 * method's week, and each level a tally of the sets finished. Adult only: the server answers an empty list to
 * a kid register, and the client sends the kid to the shelf rather than show
 * an empty page.
 */
export function LessonLibrary() {
  const today = localDay(new Date());
  // Bumped when a set is started, so the badge, the card and the list all
  // read the new "this week" from the server rather than from a patched store.
  const [fetchKey, setFetchKey] = useState(0);
  const data = useLazyLoadQuery<LessonLibraryQueryType>(LessonLibraryQuery, {
    localDay: today,
    weekStart: addDays(today, -6)
  }, {
    fetchPolicy: 'store-and-network',
    fetchKey
  });
  const [start, starting] = useMutation<LessonLibraryStartMutation>(StartSet);
  const learner = data.me?.learner;
  if (registerFor(learner?.defaultCharacter?.audience) === 'kid') return <Navigate to="/books" replace />;
  const sets = learner?.lessonSets ?? [];
  const groups = levelGroups(sets);
  const activeId = learner?.activeLessonSet?.id ?? null;
  return <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4">
      <h1 className="text-2xl font-bold">{t('lessons-title')}</h1>
      <QuietBoundary>
        <Suspense fallback={null}>
          <LessonTodayCard fetchKey={fetchKey} />
        </Suspense>
      </QuietBoundary>
      {sets.length > 0 && <WeekStrip days={weekStrip(today, learner?.lessonDays ?? [])} />}
      {sets.length === 0 && <p className="text-base-content/60">{t('lessons-empty')}</p>}
      {groups.map(g => {
      const tally = levelSummary(g.sets);
      return <section key={g.level ?? 'none'} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-lg font-bold">
                  {g.level === null ? t('lessons-level-other') : t('lessons-level', {
                level: g.level
              })}
                </h2>
                <span className="text-base-content/60 text-sm">
                  {t('lessons-level-progress', {
                finished: tally.finished,
                total: tally.total
              })}
                </span>
              </div>
              <progress aria-hidden className="progress progress-primary h-1.5" value={tally.finished} max={Math.max(1, tally.total)} />
            </div>
            <ul className="flex flex-col gap-2">
              {g.sets.map(s => {
            const active = s.id === activeId;
            return <li key={s.id} className={`bg-base-100 flex items-center gap-3 rounded-box border p-3 ${active ? 'border-primary/40 bg-primary/5' : 'border-base-200'}`}>
                    <SetRing daysPlayed={s.daysPlayed} />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <Link to={`/lessons/${encodeURIComponent(s.id)}`} className="flex min-h-(--hit-min) items-center font-semibold">
                        <En>{s.name}</En>
                      </Link>
                      {active && <span className="badge badge-primary badge-sm self-start">
                          {s.day == null ? t('lessons-this-week') : t('lessons-this-week-day', {
                    day: s.day
                  })}
                        </span>}
                      <div className="flex flex-wrap gap-1">
                        {s.lessons.map(l => <span key={l.id} title={t(kindLabel(l.kind))}>
                            <KindTile kind={l.kind} size="sm" />
                            <span className="sr-only">{t(kindLabel(l.kind))}</span>
                          </span>)}
                      </div>
                    </div>
                    {!active && <button className="btn btn-sm btn-ghost text-primary min-h-(--hit-min) shrink-0" disabled={starting} onClick={() => start({
                variables: {
                  input: {
                    lessonSetId: s.id
                  },
                  localDay: today
                },
                onCompleted: () => setFetchKey(k => k + 1)
              })}>
                        {t('lessons-start-set')}
                      </button>}
                  </li>;
          })}
            </ul>
          </section>;
    })}
    </div>;
}